import { useState, useEffect } from 'react';
import { X, Upload as UploadIcon, FileText, AlertCircle } from 'lucide-react';
import api from '../api';
import { formatApiErrorDetail } from '../utils/apiError';

export default function JDForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    client_name: '',
    role_title: '',
    experience_range: '',
    location: '',
    requirement_type: 'Ongoing',
    deadline: '',
    openings: 1,
    assignee_id: '',  // Legacy single assignee (for backward compatibility)
    manual_description: ''
  });
  
  const [file, setFile] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);  // Task 18: Multi-select
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    fetchAssignableUsers();
  }, []);
  
  // Task 17: Fetch assignable users based on RBAC
  const fetchAssignableUsers = async () => {
    try {
      const response = await api.get('/users/assignee-list');
      setAssignableUsers(response.data.assignable_users);
      setCurrentUserRole(response.data.current_user_role);
      console.log(`[JD Form] Loaded ${response.data.assignable_users.length} assignable users for role: ${response.data.current_user_role}`);
    } catch (err) {
      console.error('Error fetching assignable users:', err);
      setError('Failed to load assignable users');
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Task 18: Handle assignee multi-select (for managers/admins)
  const handleAssigneeToggle = (userId) => {
    if (currentUserRole === 'recruiter') {
      // Recruiter can only select self (already pre-selected)
      return;
    }
    
    setSelectedAssignees(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file size (5 MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5 MB limit');
        return;
      }
      
      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx'];
      const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      if (!allowedTypes.includes(fileExt)) {
        setError(`Invalid file type. Allowed: ${allowedTypes.join(', ')}`);
        return;
      }
      
      setFile(selectedFile);
      setError(null);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!formData.client_name.trim()) {
      setError('Client Name is required');
      return;
    }
    
    if (!formData.role_title.trim()) {
      setError('Role Title is required');
      return;
    }
    
    if (!file && !formData.manual_description) {
      setError('Either file upload or manual description is required');
      return;
    }
    
    if (formData.requirement_type === 'Time-bound' && !formData.deadline) {
      setError('Deadline is required for Time-bound requirements');
      return;
    }
    
    setLoading(true);
    
    try {
      const data = new FormData();
      data.append('client_name', formData.client_name);
      data.append('role_title', formData.role_title);
      if (formData.experience_range) data.append('experience_range', formData.experience_range);
      if (formData.location) data.append('location', formData.location);
      data.append('requirement_type', formData.requirement_type);
      if (formData.deadline) data.append('deadline', formData.deadline);
      data.append('openings', formData.openings);
      // Task 18: Use selectedAssignees (multi-select) or fall back to assignee_id
      if (selectedAssignees.length > 0) {
        // Use first selected as primary assignee for backward compatibility
        data.append('assignee_id', selectedAssignees[0].toString());
      } else if (formData.assignee_id && formData.assignee_id !== '') {
        // Legacy single select
        data.append('assignee_id', formData.assignee_id.toString());
      }
      if (formData.manual_description) data.append('manual_description', formData.manual_description);
      if (file) data.append('file', file);
      
      // Override the shared JSON default so Axios preserves FormData and the browser adds its boundary.
      await api.post('/jds/upload', data, {
        headers: { 'Content-Type': false }
      });
      
      onSuccess?.();
      onClose();
    } catch (err) {
      // Task 16E: Handle validation errors properly (arrays or strings)
      const detail = err.response?.data?.detail;
      const message = formatApiErrorDetail(detail);
      
      setError(message);
      console.error('❌ JD Upload Error:', err.response?.data);
      console.error('Full error:', err);
      
      // Task 16F: Log validation errors in detail
      if (Array.isArray(detail)) {
        console.error('Validation errors:');
        detail.forEach((error, idx) => {
          console.error(`  ${idx + 1}. Field: ${error.loc?.join('.')}, Message: ${error.msg}, Type: ${error.type}`);
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-navy-dark border border-teal/30 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-teal/30">
          <h2 className="text-2xl font-heading font-bold text-gold">Upload Job Description</h2>
          <button onClick={onClose} className="text-textLight/60 hover:text-red-400 transition-colors">
            <X size={24} />
          </button>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start space-x-3">
              <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            {/* Client Name */}
            <div>
              <label className="block text-sm font-medium text-textLight mb-2">Client Name <span className="text-red-400">*</span></label>
              <input
                type="text"
                name="client_name"
                value={formData.client_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
                placeholder="Enter client name"
              />
            </div>
            
            {/* Role Title */}
            <div>
              <label className="block text-sm font-medium text-textLight mb-2">Role Title <span className="text-red-400">*</span></label>
              <input
                type="text"
                name="role_title"
                value={formData.role_title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
                placeholder="e.g., Senior Software Engineer"
              />
            </div>
            
            {/* Experience Range */}
            <div>
              <label className="block text-sm font-medium text-textLight mb-2">Experience Range</label>
              <input
                type="text"
                name="experience_range"
                value={formData.experience_range}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
                placeholder="e.g., 3-5 years"
              />
            </div>
            
            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-textLight mb-2">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
                placeholder="e.g., Bangalore, Mumbai"
              />
            </div>
            
            {/* Openings */}
            <div>
              <label className="block text-sm font-medium text-textLight mb-2">Number of Openings</label>
              <input
                type="number"
                name="openings"
                value={formData.openings}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
              />
            </div>
            
            {/* Task 18: Multi-Assignee Selection */}
            <div>
              <label className="block text-sm font-medium text-textLight mb-2">
                Assign To {currentUserRole === 'recruiter' && <span className="text-xs text-textLight/60">(Self only)</span>}
                {currentUserRole !== 'recruiter' && <span className="text-xs text-textLight/60">(Select multiple)</span>}
              </label>
              
              {currentUserRole === 'recruiter' ? (
                /* Recruiter: Locked to self */
                <div className="px-4 py-3 bg-navy-light border border-teal/30 rounded-lg">
                  {assignableUsers.length > 0 ? (
                    <div className="flex items-center space-x-2">
                      <input type="checkbox" checked disabled className="w-4 h-4 text-teal rounded" />
                      <span className="text-textLight">{assignableUsers[0].name} (YOU)</span>
                    </div>
                  ) : (
                    <span className="text-textLight/60 text-sm">Loading...</span>
                  )}
                </div>
              ) : (
                /* Manager/Admin: Multi-select checkboxes */
                <div className="max-h-48 overflow-y-auto px-4 py-3 bg-navy-light border border-teal/30 rounded-lg space-y-2">
                  {assignableUsers.length === 0 ? (
                    <p className="text-xs text-textLight/60">Loading assignable users...</p>
                  ) : (
                    assignableUsers.map(user => (
                      <label key={user.id} className="flex items-center space-x-2 cursor-pointer hover:bg-teal/10 px-2 py-1.5 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedAssignees.includes(user.id)}
                          onChange={() => handleAssigneeToggle(user.id)}
                          className="w-4 h-4 text-teal rounded focus:ring-2 focus:ring-teal"
                        />
                        <span className="text-textLight text-sm">
                          {user.name} <span className="text-textLight/60 text-xs">({user.role.toUpperCase()})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              )}
              
              {selectedAssignees.length > 0 && (
                <p className="text-xs text-teal mt-1">{selectedAssignees.length} user(s) selected</p>
              )}
            </div>
          </div>
          
          {/* Requirement Type */}
          <div>
            <label className="block text-sm font-medium text-textLight mb-2">Requirement Type <span className="text-red-400">*</span></label>
            <div className="flex space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="requirement_type"
                  value="Ongoing"
                  checked={formData.requirement_type === 'Ongoing'}
                  onChange={handleChange}
                  className="w-4 h-4 text-teal focus:ring-2 focus:ring-teal"
                />
                <span className="text-textLight">Ongoing</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="requirement_type"
                  value="Time-bound"
                  checked={formData.requirement_type === 'Time-bound'}
                  onChange={handleChange}
                  className="w-4 h-4 text-teal focus:ring-2 focus:ring-teal"
                />
                <span className="text-textLight">Time-bound</span>
              </label>
            </div>
          </div>
          
          {/* Deadline (conditional) */}
          {formData.requirement_type === 'Time-bound' && (
            <div>
              <label className="block text-sm font-medium text-textLight mb-2">Deadline <span className="text-red-400">*</span></label>
              <input
                type="date"
                name="deadline"
                value={formData.deadline}
                onChange={handleChange}
                required={formData.requirement_type === 'Time-bound'}
                className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
              />
            </div>
          )}
          
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-textLight mb-2">Upload JD File</label>
            <div className="border-2 border-dashed border-teal/30 rounded-lg p-6 text-center hover:border-teal/50 transition-colors">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleFileChange}
                className="hidden"
                id="jd-file-upload"
              />
              <label htmlFor="jd-file-upload" className="cursor-pointer">
                <UploadIcon className="mx-auto text-teal mb-2" size={32} />
                <p className="text-textLight mb-1">
                  {file ? file.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-textLight/60 text-sm">PDF, DOC, DOCX, XLS, XLSX (max 5MB)</p>
              </label>
            </div>
          </div>
          
          {/* Manual Description */}
          <div>
            <label className="block text-sm font-medium text-textLight mb-2">
              Manual JD Description {!file && <span className="text-red-400">*</span>}
            </label>
            <textarea
              name="manual_description"
                value={formData.manual_description}
                onChange={handleChange}
                rows={6}
                className="w-full px-4 py-2 bg-navy border border-teal/30 rounded-lg text-textLight focus:ring-2 focus:ring-teal focus:border-transparent"
                placeholder="Enter job description details here..."
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-textLight/30 rounded-lg text-textLight hover:bg-navy-light transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-teal hover:bg-teal-light text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <FileText size={20} />
                    <span>Upload JD</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
